/**
 * Professional Multi-Select AutoComplete Component
 * Features: Tags, Cross-location selection, Custom entries, Keyboard navigation
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
  XMarkIcon,
  PlusIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { 
  searchInstitutions, 
  searchCountries,
  getCities,
  University, 
  Country,
  City,
  AutoSuggestResult 
} from '../services/educationApi';

export interface SelectedItem {
  id: string;
  name: string;
  type: 'university' | 'country' | 'city';
  country?: string;
  city?: string;
  isCustom?: boolean;
  domains?: string[];
}

export interface MultiSelectAutoCompleteProps {
  // Basic props
  values: SelectedItem[];
  onChange: (values: SelectedItem[]) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  
  // AutoComplete specific
  type: 'university' | 'country' | 'city';
  filterCountry?: string; // For city/university filtering
  filterCity?: string; // For university filtering
  limit?: number;
  minLength?: number;
  allowCustom?: boolean; // Allow adding custom entries
  allowCrossLocation?: boolean; // Allow universities from other countries/cities
  
  // Styling
  className?: string;
  inputClassName?: string;
  dropdownClassName?: string;
  tagClassName?: string;
  
  // Callbacks
  onSelect?: (item: SelectedItem) => void;
  onRemove?: (item: SelectedItem) => void;
  onError?: (error: string) => void;
  onSearch?: (query: string) => void;
}

interface MultiSelectState {
  isOpen: boolean;
  suggestions: (University | Country | City)[];
  loading: boolean;
  error: string | null;
  highlightedIndex: number;
  inputValue: string;
  showCustomOption: boolean;
  cached: boolean;
}

const MultiSelectAutoComplete: React.FC<MultiSelectAutoCompleteProps> = ({
  values,
  onChange,
  placeholder = "Start typing...",
  disabled = false,
  required = false,
  error,
  type,
  filterCountry,
  filterCity,
  limit = 10,
  minLength = 2,
  allowCustom = true,
  allowCrossLocation = true,
  className = '',
  inputClassName = '',
  dropdownClassName = '',
  tagClassName = '',
  onSelect,
  onRemove,
  onError,
  onSearch
}) => {
  // State management
  const [state, setState] = useState<MultiSelectState>({
    isOpen: false,
    suggestions: [],
    loading: false,
    error: null,
    highlightedIndex: -1,
    inputValue: '',
    showCustomOption: false,
    cached: false
  });

  // Refs
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const suggestionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const searchTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  // Search function
  const performSearch = useCallback(async (query: string) => {
    if (!query || query.trim().length < minLength) {
      setState(prev => ({
        ...prev,
        suggestions: [],
        isOpen: false,
        loading: false,
        error: null,
        showCustomOption: false
      }));
      return;
    }

    const normalizedQuery = query.trim();
    setState(prev => ({ ...prev, loading: true, error: null }));
    onSearch?.(normalizedQuery);

    try {
      let results: (University | Country | City)[] = [];
      let cached = false;

      if (type === 'university') {
        const response: AutoSuggestResult = await searchInstitutions(
          normalizedQuery, 
          allowCrossLocation ? undefined : filterCountry, 
          limit
        );
        results = response.institutions;
        cached = response.cached;
      } else if (type === 'country') {
        results = await searchCountries(normalizedQuery);
      } else if (type === 'city') {
        results = await getCities(normalizedQuery, filterCountry);
      }

      // Filter out already selected items
      const selectedNames = values.map(v => v.name.toLowerCase());
      const filteredResults = results.filter(item => 
        !selectedNames.includes(item.name.toLowerCase())
      );

      // Show custom option if allowed and no exact match
      const exactMatch = filteredResults.some(item => 
        item.name.toLowerCase() === normalizedQuery.toLowerCase()
      );
      
      const showCustom = allowCustom && !exactMatch && normalizedQuery.length >= minLength;

      setState(prev => ({
        ...prev,
        suggestions: filteredResults,
        isOpen: filteredResults.length > 0 || showCustom,
        loading: false,
        error: null,
        highlightedIndex: -1,
        showCustomOption: showCustom,
        cached
      }));

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Search failed';
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
        suggestions: [],
        isOpen: false,
        showCustomOption: false
      }));
      onError?.(errorMessage);
    }
  }, [type, filterCountry, filterCity, limit, minLength, allowCustom, allowCrossLocation, values, onSearch, onError]);

  // Debounced search
  const debouncedSearch = useCallback((query: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);
  }, [performSearch]);

  // Handle input change
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setState(prev => ({ ...prev, inputValue: newValue }));
    debouncedSearch(newValue);
  }, [debouncedSearch]);

  // Handle suggestion selection
  const handleSelect = useCallback((item: University | Country | City | null) => {
    let selectedItem: SelectedItem;

    if (!item) {
      // Custom entry
      selectedItem = {
        id: `custom_${Date.now()}_${Math.random()}`,
        name: state.inputValue.trim(),
        type: type as 'university' | 'country' | 'city',
        isCustom: true,
        country: type === 'university' ? filterCountry : undefined,
        city: type === 'university' ? filterCity : undefined
      };
    } else {
      selectedItem = {
        id: `${type}_${item.name}_${Date.now()}`,
        name: item.name,
        type: type as 'university' | 'country' | 'city',
        country: 'country' in item ? item.country : filterCountry,
        city: type === 'university' ? filterCity : undefined,
        domains: 'domains' in item ? item.domains : undefined,
        isCustom: false
      };
    }
    
    const newValues = [...values, selectedItem];
    onChange(newValues);
    onSelect?.(selectedItem);
    
    setState(prev => ({
      ...prev,
      isOpen: false,
      highlightedIndex: -1,
      inputValue: '',
      suggestions: [],
      showCustomOption: false
    }));
    
    // Clear input and focus for next entry
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.focus();
    }
  }, [state.inputValue, type, filterCountry, filterCity, values, onChange, onSelect]);

  // Handle tag removal
  const handleRemove = useCallback((itemToRemove: SelectedItem) => {
    const newValues = values.filter(item => item.id !== itemToRemove.id);
    onChange(newValues);
    onRemove?.(itemToRemove);
  }, [values, onChange, onRemove]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const totalOptions = state.suggestions.length + (state.showCustomOption ? 1 : 0);

    if (!state.isOpen && e.key === 'ArrowDown' && totalOptions > 0) {
      setState(prev => ({ ...prev, isOpen: true, highlightedIndex: 0 }));
      e.preventDefault();
      return;
    }

    if (!state.isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setState(prev => ({
          ...prev,
          highlightedIndex: Math.min(prev.highlightedIndex + 1, totalOptions - 1)
        }));
        break;

      case 'ArrowUp':
        e.preventDefault();
        setState(prev => ({
          ...prev,
          highlightedIndex: Math.max(prev.highlightedIndex - 1, -1)
        }));
        break;

      case 'Enter':
        e.preventDefault();
        if (state.highlightedIndex >= 0) {
          if (state.highlightedIndex < state.suggestions.length) {
            handleSelect(state.suggestions[state.highlightedIndex]);
          } else if (state.showCustomOption) {
            handleSelect(null); // Custom entry
          }
        }
        break;

      case 'Escape':
        setState(prev => ({ ...prev, isOpen: false, highlightedIndex: -1, inputValue: '' }));
        if (inputRef.current) {
          inputRef.current.value = '';
          inputRef.current.blur();
        }
        break;

      case 'Backspace':
        if (state.inputValue === '' && values.length > 0) {
          // Remove last tag when backspace on empty input
          handleRemove(values[values.length - 1]);
        }
        break;
    }
  }, [state.isOpen, state.highlightedIndex, state.suggestions, state.showCustomOption, state.inputValue, values, handleSelect, handleRemove]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setState(prev => ({ ...prev, isOpen: false, highlightedIndex: -1 }));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (state.highlightedIndex >= 0 && suggestionRefs.current[state.highlightedIndex]) {
      suggestionRefs.current[state.highlightedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [state.highlightedIndex]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Render suggestion item
  const renderSuggestion = (item: University | Country | City, index: number) => {
    const isHighlighted = index === state.highlightedIndex;
    const isUniversity = 'domains' in item;
    const isCountry = 'code' in item && 'flag' in item;
    
    return (
      <div
        key={`${item.name}-${index}`}
        ref={el => { suggestionRefs.current[index] = el; }}
        className={`
          px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0
          ${isHighlighted ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}
          transition-colors duration-150
        `}
        onClick={() => handleSelect(item)}
        onMouseEnter={() => setState(prev => ({ ...prev, highlightedIndex: index }))}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="font-medium text-sm flex items-center">
              {isCountry && (item as Country).flag && (
                <span className="mr-2">{(item as Country).flag}</span>
              )}
              {isUniversity && <GlobeAltIcon className="w-4 h-4 mr-2 text-gray-400" />}
              {item.name}
            </div>
            {isUniversity && (item as University).country && (
              <div className="text-xs text-gray-500 mt-1">
                📍 {(item as University).country}
                {allowCrossLocation && filterCountry && (item as University).country !== filterCountry && (
                  <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                    Different Country
                  </span>
                )}
              </div>
            )}
          </div>
          {isHighlighted && (
            <CheckIcon className="w-4 h-4 text-blue-600 ml-2" />
          )}
        </div>
      </div>
    );
  };

  // Render custom option
  const renderCustomOption = () => {
    const isHighlighted = state.highlightedIndex === state.suggestions.length;
    
    return (
      <div
        key="custom-option"
        ref={el => { suggestionRefs.current[state.suggestions.length] = el; }}
        className={`
          px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0
          ${isHighlighted ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-50'}
          transition-colors duration-150
        `}
        onClick={() => handleSelect(null)}
        onMouseEnter={() => setState(prev => ({ ...prev, highlightedIndex: state.suggestions.length }))}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="font-medium text-sm flex items-center">
              <PlusIcon className="w-4 h-4 mr-2 text-green-500" />
              Add "{state.inputValue}"
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Custom {type} entry
            </div>
          </div>
          {isHighlighted && (
            <CheckIcon className="w-4 h-4 text-green-600 ml-2" />
          )}
        </div>
      </div>
    );
  };

  // Render selected tags
  const renderTags = () => {
    if (values.length === 0) return null;

    return (
      <div className="flex flex-wrap gap-2 mb-2">
        {values.map((item) => (
          <div
            key={item.id}
            className={`
              inline-flex items-center px-3 py-1 rounded-full text-sm font-medium
              ${item.isCustom 
                ? 'bg-green-100 text-green-800 border border-green-200' 
                : 'bg-blue-100 text-blue-800 border border-blue-200'
              }
              ${tagClassName}
            `}
          >
            {item.isCustom && <TagIcon className="w-3 h-3 mr-1" />}
            <span>{item.name}</span>
            {item.country && item.country !== item.name && (
              <span className="ml-1 text-xs opacity-75">({item.country})</span>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                handleRemove(item);
              }}
              className="ml-2 text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <XMarkIcon className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Selected Tags */}
      {renderTags()}

      {/* Input Field */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={state.inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (state.suggestions.length > 0 || state.showCustomOption) {
              setState(prev => ({ ...prev, isOpen: true }));
            }
          }}
          placeholder={values.length === 0 ? placeholder : `Add another ${type}...`}
          disabled={disabled}
          required={required && values.length === 0}
          className={`
            w-full px-4 py-2 pr-10 border rounded-md
            ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'}
            ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
            focus:outline-none focus:ring-2 focus:ring-opacity-50
            transition-colors duration-200
            ${inputClassName}
          `}
        />
        
        {/* Loading/Search Icon */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {state.loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          ) : state.error ? (
            <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />
          ) : (
            <MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Error Message */}
      {(error || state.error) && (
        <p className="mt-1 text-sm text-red-600">
          {error || state.error}
        </p>
      )}

      {/* Dropdown */}
      {state.isOpen && (state.suggestions.length > 0 || state.showCustomOption) && (
        <div
          ref={dropdownRef}
          className={`
            absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg
            max-h-60 overflow-y-auto
            ${dropdownClassName}
          `}
        >
          {/* Cache indicator */}
          {state.cached && (
            <div className="px-4 py-2 bg-green-50 border-b border-green-100 text-green-700 text-xs">
              <CheckIcon className="w-3 h-3 inline mr-1" />
              Cached results
            </div>
          )}
          
          {/* Suggestions */}
          {state.suggestions.map((item, index) => renderSuggestion(item, index))}
          
          {/* Custom option */}
          {state.showCustomOption && renderCustomOption()}
          
          {/* Results info */}
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-gray-500 text-xs text-center">
            {state.suggestions.length > 0 && `${state.suggestions.length} suggestions`}
            {state.showCustomOption && state.suggestions.length > 0 && " • "}
            {state.showCustomOption && "Press Enter to add custom"}
            {allowCrossLocation && type === 'university' && (
              <div className="mt-1 text-yellow-600">
                💡 Universities from all countries shown
              </div>
            )}
          </div>
        </div>
      )}

      {/* No results message */}
      {state.isOpen && !state.loading && state.suggestions.length === 0 && !state.showCustomOption && state.inputValue.length >= minLength && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-4 text-center text-gray-500">
          No {type}s found
          {allowCustom && (
            <div className="mt-2">
              <button
                onClick={() => handleSelect(null)}
                className="text-blue-600 hover:text-blue-800 text-sm underline"
              >
                Add "{state.inputValue}" as custom {type}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectAutoComplete;
